import React, { useEffect, useState, useRef } from "react";
import {
  getActiveParticipantsAsync,
  registerExistingParticipant,
  getWaivers,
  toggleParticipantDeactivated,
} from "../../../../services/participantServices/ParticipantService";
import { getAllRegions } from "../../../../services/userServices/UserInfoService";
import { useAuth } from "../../../../firebase/AuthProvider";
import { Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ActiveParticipantDTO,
  GetAllActiveParticipantsRequest,
} from "../../../../interfaces/Participant";
import ParticipantElementsFilter from "./ParticipantElementsFilter";
import { ROLES, ROLE_PRIORITY } from "../../../../Data/SD";
import { EmailModal } from "./Modal/EmailModal";
import TransferParticipantModal from "./TransferParticipantModal";
import BumpParticipantModal from "./BumpParticipantModal";
import { showConfirmationDialog } from "../../../modal/ConfimationModal/Confimation";
import AddParticipantModal from "./AddParticipantModal";
import EditParticipantModal from "./EditParticipantModal";
import { getTeamBySupervisor } from "../../../../services/userServices/TeamService";

interface OptionType {
  value: string;
  label: string;
}

interface CaseworkerSection {
  id: string;
  name: string;
  participantCount: number;
  participants: ActiveParticipantDTO[];
  isExpanded: boolean;
  isLoading: boolean;
  roles?: string[];
}

const BUMP_BUTTON_ALLOWED_FOR_WAIVER_OPTIONS: string[] = ["EPSDT", "OCDD-CC", "OCDD-SW", "OCDD-ROW", "OAAS-ADHC"];

export const ROLE_MAP: Record<string, string> = {
  "program manager": "PM",
  "supervisor": "Supervisor",
  "owner": "Executive Director",
  "sc": "SC",
  "assessor": "Assessor",
  "admin": "Admin",
  "developer": "DEV",
  "scrum master": "SM"
};

export const HEADER_ROLE_MAP: Record<string, string> = {
  "program manager": "Program Manager",
  "supervisor": "Supervisor",
  "owner": "Executive Director",
  "sc": "Support Coordinator",
  "assessor": "Assessor",
  "admin": "Admin",
  "developer": "Developer",
  "scrum master": "Scrum Master"
};

const OwnerParticipantsElement: React.FC = () => {
  const [caseworkerSections, setCaseworkerSections] = useState<CaseworkerSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [totalActiveParticipants, setTotalActiveParticipants] = useState<number>(0);

  const { currentUser, currentRoles } = useAuth();
  const navigate = useNavigate();

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [isTransferParticipantModalOpen, setIsTransferParticipantModalOpen] = useState<boolean>(false);
  const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
  const [isEditParticipantModalOpen, setIsEditParticipantModalOpen] = useState(false);
  const [editingParticipantId, setEditingParticipantId] = useState<number | null>(null);
  const [isBumpParticipantModalOpen, setIsBumpParticipantModalOpen] = useState(false);
  const [bumpingParticipant, setBumpingParticipant] = useState<ActiveParticipantDTO | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);

  // Active Filter States (Used for Fetching)
  const [waiverOptions, setWaiverOptions] = useState<OptionType[]>([]);
  const [regionOptions, setRegionOptions] = useState<OptionType[]>([]);
  const [caseworkerOptions, setCaseworkerOptions] = useState<OptionType[]>([]);
  const [selectedWaiverIds, setSelectedWaiverIds] = useState<string[]>([]);
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);
  const [selectedCaseworkerIds, setSelectedCaseworkerIds] = useState<string[]>([]);
  const [selectedEmailLinked, setSelectedEmailLinked] = useState<string>("");

  // Pending Filter States (Used in Filter Modal)
  const [pendingWaiverIds, setPendingWaiverIds] = useState<string[]>([]);
  const [pendingRegionId, setPendingRegionId] = useState<number | null>(null);
  const [pendingCaseworkerIds, setPendingCaseworkerIds] = useState<string[]>([]);
  const [pendingEmailLinked, setPendingEmailLinked] = useState<string>("");

  const [participantToggles, setParticipantToggles] = useState<{ [id: string]: boolean }>({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [selectedParticipantForTransfer, setSelectedParticipantForTransfer] = useState<ActiveParticipantDTO | null>(null);
  const menuButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const buildRequestFromFilters = (
    caseworkerId: string
  ): GetAllActiveParticipantsRequest => ({
    pageNumber: 1,
    pageSize: 1000,
    searchTeam:
      debouncedSearchTerm && debouncedSearchTerm.trim().length > 0
        ? debouncedSearchTerm.trim()
        : null,
    waiverTypeIds:
      selectedWaiverIds.length > 0 ? selectedWaiverIds.map(Number) : null,
    regionId: selectedRegionId ?? null,
    linkedAccount:
      selectedEmailLinked === ""
        ? null
        : selectedEmailLinked === "yes"
          ? true
          : false,
    caseworkerIds: [caseworkerId],
    sortColumn,
    sortDirection,
    participantsUnderMe: !currentRoles?.some(
      (role) => role.name === ROLES.OWNER
    ),
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const handleOpenFilters = () => {
    // Sync pending state with active state when opening modal
    setPendingWaiverIds(selectedWaiverIds);
    setPendingRegionId(selectedRegionId);
    setPendingCaseworkerIds(selectedCaseworkerIds);
    setPendingEmailLinked(selectedEmailLinked);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    // Commit pending state to active state
    setSelectedWaiverIds(pendingWaiverIds);
    setSelectedRegionId(pendingRegionId);
    setSelectedCaseworkerIds(pendingCaseworkerIds);
    setSelectedEmailLinked(pendingEmailLinked);
    setShowFilters(false);
  };

  const handleSaveEmail = async () => {
    try {
      if (!currentUser) return;
      const authToken = await currentUser.getIdToken();
      const trimmedEmail = selectedEmail.trim();
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

      if (!trimmedEmail) {
        toast.error("Email is required");
        return;
      }
      if (!isValidEmail) {
        toast.error("Please enter a valid email address.");
        return;
      }

      const response = await registerExistingParticipant(
        authToken,
        selectedParticipant,
        trimmedEmail
      );

      if (response.success) {
        toast.success(response.message);
        const expandedSection = caseworkerSections.find((s) => s.isExpanded);
        if (expandedSection) {
          await fetchParticipantsForCaseworker(expandedSection.id);
        }
      } else {
        toast.error(response.message || "Failed to link email");
      }
      setIsEmailModalOpen(false);
    } catch (err: any) {
      toast.error(err?.message || "Failed to link the email");
    }
  };

  useEffect(() => {
    if (selectedParticipant !== 0) {
      setSelectedEmail("");
      setIsEmailModalOpen(true);
    }
  }, [selectedParticipant]);

  const fetchCaseworkersWithCounts = async () => {
    try {
      if (!currentUser) return;

      // Check if ANY filter is active (not just search term)
      const hasActiveFilters =
        debouncedSearchTerm.trim().length > 0 ||
        selectedWaiverIds.length > 0 ||
        selectedRegionId !== null ||
        selectedEmailLinked !== "" ||
        selectedCaseworkerIds.length > 0;

      if (caseworkerSections.length === 0) {
        setLoading(true);
      } else {
        setIsSearching(true);
      }

      const authToken = await currentUser.getIdToken();
      let usersRes = await getTeamBySupervisor(authToken);

      if (usersRes) {
        // 1. If Caseworker filter is selected, filter the sections list immediately
        if (selectedCaseworkerIds.length > 0) {
          usersRes = usersRes.filter((user: any) =>
            selectedCaseworkerIds.includes(user.id.toString())
          );
        }

        // Calculate total participants (based on filtered caseworker list)
        const total = usersRes.reduce((sum: number, u: any) => sum + (u.participantCount || 0), 0);
        setTotalActiveParticipants(total);

        const newSections: CaseworkerSection[] = [];

        for (const user of usersRes) {
          const existingSection = caseworkerSections.find(s => s.id === user.id.toString());
          // If filtering, collapse initially until we verify they match the filter
          // If NOT filtering, preserve previous expanded state
          const shouldBeExpanded = hasActiveFilters ? false : (existingSection?.isExpanded || false);

          let roleNames = user?.role ? new Array(user?.role) : [];

          newSections.push({
            id: user.id.toString(),
            name: `${user.firstName} ${user.lastName}`,
            participantCount: user.participantCount || 0,
            participants: hasActiveFilters ? [] : (existingSection?.participants || []),
            isExpanded: shouldBeExpanded,
            isLoading: hasActiveFilters,
            roles: roleNames,
          });
        }

        if (hasActiveFilters) {
          // 2. Search Mode: Fetch participants for ALL sections to see if they match filters
          const promises = newSections.map(async (section) => {
            try {
              const req = buildRequestFromFilters(section.id);
              const res = await getActiveParticipantsAsync(authToken, req);

              if (res.data.items.length > 0) {
                const newToggles: { [id: string]: boolean } = {};
                res.data.items.forEach((p: ActiveParticipantDTO) => {
                  if (participantToggles[p.id] === undefined) {
                    newToggles[p.id] = true;
                  }
                });
                setParticipantToggles((prev) => ({ ...prev, ...newToggles }));

                return {
                  ...section,
                  participants: res.data.items,
                  participantCount: res.data.totalCount || res.data.items.length, // Update count to show match count
                  isExpanded: true, // Auto-expand matches
                  isLoading: false
                };
              } else {
                return {
                  ...section,
                  participants: [],
                  participantCount: 0,
                  isExpanded: false,
                  isLoading: false
                }
              }
            } catch (error) {
              console.error(`Failed to search for ${section.name}`, error);
              return { ...section, isLoading: false };
            }
          });

          const resolvedSections = await Promise.all(promises);
          // Filter out sections that have no matching participants so the user sees results
          const filteredSections = resolvedSections.filter(section => section.participants.length > 0);
          setCaseworkerSections(filteredSections);

        } else {
          // 3. Lazy Load Mode: Only fetch if section is already expanded
          const promises = newSections.map(async (section) => {
            if (section.isExpanded) {
              try {
                section.isLoading = true;
                const req = buildRequestFromFilters(section.id);
                const res = await getActiveParticipantsAsync(authToken, req);
                section.participants = res.data.items;
                section.isLoading = false;

                const newToggles: { [id: string]: boolean } = {};
                res.data.items.forEach((p: ActiveParticipantDTO) => {
                  if (participantToggles[p.id] === undefined) {
                    newToggles[p.id] = true;
                  }
                });
                setParticipantToggles((prev) => ({ ...prev, ...newToggles }));
              } catch (e) {
                section.isLoading = false;
              }
            }
            return section;
          });
          const resolvedSections = await Promise.all(promises);
          setCaseworkerSections(resolvedSections);
        }
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to fetch caseworkers");
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const fetchParticipantsForCaseworker = async (caseworkerId: string) => {
    try {
      if (!currentUser) return;

      setCaseworkerSections((prev) =>
        prev.map((section) =>
          section.id === caseworkerId
            ? { ...section, isLoading: true }
            : section
        )
      );

      const authToken = await currentUser.getIdToken();
      const req = buildRequestFromFilters(caseworkerId);
      const res = await getActiveParticipantsAsync(authToken, req);

      const newToggles: { [id: string]: boolean } = {};
      res.data.items.forEach((p: ActiveParticipantDTO) => {
        if (participantToggles[p.id] === undefined) {
          newToggles[p.id] = true;
        }
      });
      setParticipantToggles((prev) => ({ ...prev, ...newToggles }));

      setCaseworkerSections((prev) =>
        prev.map((section) =>
          section.id === caseworkerId
            ? {
              ...section,
              participants: res.data.items,
              participantCount: res.data.totalCount || section.participantCount,
              isLoading: false,
            }
            : section
        )
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to fetch participants");
      setCaseworkerSections((prev) =>
        prev.map((section) =>
          section.id === caseworkerId
            ? { ...section, isLoading: false }
            : section
        )
      );
    }
  };

  const toggleSection = async (caseworkerId: string) => {
    const section = caseworkerSections.find((s) => s.id === caseworkerId);
    if (!section) return;

    if (!section.isExpanded && section.participants.length === 0) {
      await fetchParticipantsForCaseworker(caseworkerId);
    }

    setCaseworkerSections((prev) =>
      prev.map((s) =>
        s.id === caseworkerId ? { ...s, isExpanded: !s.isExpanded } : s
      )
    );
  };

  const handleToggleParticipant = async (participantId: string) => {
    const currentState = participantToggles[participantId];

    if (currentState) {
      const isConfirm = await showConfirmationDialog(
        "Are you sure want to deactivate participant?",
        "User deactivation will revoke system access and remove this user from all task view, filters, and assignment options. Any participant assigned to this user must be reassigned prior to deactivation."
      );
      if (!isConfirm) return;
    }

    try {
      if (!currentUser) return;
      const authToken = await currentUser.getIdToken();
      const result = await toggleParticipantDeactivated(
        participantId.toString(),
        authToken
      );

      if (result.success) {
        setParticipantToggles((prev) => ({
          ...prev,
          [participantId]: !currentState,
        }));
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error("Failed to update participant status.");
      setParticipantToggles((prev) => ({
        ...prev,
        [participantId]: currentState,
      }));
    }
  };

  useEffect(() => {
    const fetchFilters = async () => {
      if (!currentUser) return;
      try {
        const authToken = await currentUser.getIdToken();

        const waiversRes = await getWaivers(authToken);
        if (waiversRes && waiversRes.success) {
          const formattedWaivers = waiversRes.data.map((w: any) => ({
            value: w.id.toString(),
            label: w.waiverName,
          }));
          setWaiverOptions(formattedWaivers);
        }

        const regionsRes = await getAllRegions(authToken, true);
        if (regionsRes && regionsRes.success) {
          const formattedRegions = regionsRes.data.map((r: any) => ({
            value: r.regionId.toString(),
            label: r.regionName,
          }));
          setRegionOptions([{ value: "", label: "All" }, ...formattedRegions]);
        }

        const usersRes = await getTeamBySupervisor(authToken);
        if (usersRes) {
          const formattedUsers = usersRes.map((u: any) => ({
            value: u.id.toString(),
            label: `${u.firstName} ${u.lastName}`,
          }));
          setCaseworkerOptions(formattedUsers);
        }
      } catch (err: any) {
        console.error("Error fetching filters:", err);
      }
    };
    fetchFilters();
  }, [currentUser]);

  useEffect(() => {
    fetchCaseworkersWithCounts();
  }, [
    currentUser,
    sortColumn,
    sortDirection,
    selectedWaiverIds,
    selectedRegionId,
    selectedEmailLinked,
    debouncedSearchTerm,
    selectedCaseworkerIds,
  ]);

  // Close menu when clicking outside and position menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId !== null) {
        const menuContainer = menuRefs.current[openMenuId];
        const button = menuButtonRefs.current[openMenuId];
        const target = event.target as Node;

        if (menuContainer && button && !menuContainer.contains(target) && !button.contains(target)) {
          setOpenMenuId(null);
        }
      }
    };

    if (openMenuId !== null) {
      const button = menuButtonRefs.current[openMenuId];
      const menu = menuRefs.current[openMenuId];

      if (button && menu) {
        const rect = button.getBoundingClientRect();
        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.right = `${window.innerWidth - rect.right}px`;
      }

      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const linkEmail = (participantId: number) => {
    setSelectedParticipant(participantId);
    setSelectedEmail("");
    setIsEmailModalOpen(true);
  };

  const openAddParticipantModal = () => {
    setIsAddParticipantModalOpen(true);
  };

  const openTransferParticipantModal = () => {
    const allParticipants: ActiveParticipantDTO[] = [];
    caseworkerSections.forEach((section) => {
      if (section.isExpanded && section.participants.length > 0) {
        allParticipants.push(...section.participants);
      }
    });

    if (allParticipants.length === 0) {
      toast.info("Please expand a caseworker section and ensure participants are loaded to transfer");
      return;
    }

    setSelectedParticipantForTransfer(null);
    setIsTransferParticipantModalOpen(true);
  };

  const getAllParticipantsForTransfer = (): ActiveParticipantDTO[] => {
    const allParticipants: ActiveParticipantDTO[] = [];
    caseworkerSections.forEach((section) => {
      if (section.isExpanded && section.participants.length > 0) {
        allParticipants.push(...section.participants);
      }
    });
    return allParticipants;
  };

  const getHighestRole = (roles: string[]): string | null => {
    if (!roles || roles.length === 0) return null;
    const highestRawRole = roles.reduce((highest, role) => {
      const highestPriority = ROLE_PRIORITY[highest] || 0;
      const currentPriority = ROLE_PRIORITY[role] || 0;
      return currentPriority > highestPriority ? role : highest;
    });
    return formatRoleName(highestRawRole);
  };

  const formatRoleName = (role: string): string => {
    if (!role) return "";
    const normalizedRole = role.toLowerCase().trim();
    return ROLE_MAP[normalizedRole] ?? role;
  };

  const handleParticipantCreated = async () => {
    await fetchCaseworkersWithCounts();
  };

  return (
    <>
      <div className="common_card">
        <div className="top top_bar_flx" style={{ alignItems: "center", marginBottom: "0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <svg style={{
                position: "absolute",
                left: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }} width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.68296 14.9968C12.1705 14.9968 14.9978 12.1696 14.9978 8.68199C14.9978 5.19442 12.1705 2.36719 8.68296 2.36719C5.1954 2.36719 2.36816 5.19442 2.36816 8.68199C2.36816 12.1696 5.1954 14.9968 8.68296 14.9968Z" stroke="#99A1AF" stroke-width="1.5787" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M16.5758 16.5768L13.1816 13.1826" stroke="#99A1AF" stroke-width="1.5787" stroke-linecap="round" stroke-linejoin="round" />
              </svg>

              <Form.Control
                type="text"
                placeholder="Search by name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  minWidth: "25rem",
                  width: "33rem",
                  height: "2.5rem",
                  paddingLeft: "2.75rem",
                  paddingRight: "0.75rem",
                  paddingTop: "0.5rem",
                  paddingBottom: "0.5rem",
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.74rem",
                  backgroundColor: "#FFFFFF",
                  color: "#111827",
                }}
                className="search-input-placeholder"
              />
              {isSearching && (
                <div style={{ position: 'absolute', right: '10px' }}>
                  <Spinner animation="border" size="sm" variant="secondary" />
                </div>
              )}
            </div>

            {/* Added Total Count Display */}
            {totalActiveParticipants > 0 && (
              <span style={{ fontSize: "0.875rem", color: "#6B7280", whiteSpace: "nowrap" }}>
                ({totalActiveParticipants} participants)
              </span>
            )}

            <Button
              variant="light"
              onClick={handleOpenFilters}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.036rem",
                gap: "0.5rem",
                border: "1px solid #E5E7EB",
                borderRadius: "0.74rem",
                backgroundColor: "#FFFFFF",
                color: "#364153",
                fontWeight: 400,
                whiteSpace: "nowrap",
                paddingTop: "0.5rem",
                paddingBottom: "0.5rem",
                height: "2.5rem",
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
                <path d="M7.10407 14.208C7.104 14.3547 7.1448 14.4985 7.22191 14.6233C7.29901 14.7481 7.40936 14.8489 7.54058 14.9145L9.11928 15.7038C9.23965 15.764 9.3734 15.7924 9.50783 15.7863C9.64226 15.7802 9.7729 15.7398 9.88734 15.669C10.0018 15.5983 10.0962 15.4994 10.1617 15.3818C10.2272 15.2643 10.2615 15.1319 10.2615 14.9974V9.47191C10.2616 9.0807 10.4071 8.70348 10.6696 8.41339L16.371 2.10728C16.4732 1.99405 16.5404 1.85365 16.5645 1.70304C16.5886 1.55243 16.5685 1.39807 16.5067 1.25863C16.4449 1.11918 16.3441 1.00063 16.2163 0.917294C16.0886 0.833961 15.9394 0.789422 15.7869 0.789062H1.57862C1.42596 0.789118 1.2766 0.833436 1.14862 0.91665C1.02064 0.999864 0.919532 1.1184 0.857549 1.25791C0.795565 1.39741 0.775363 1.5519 0.799389 1.70265C0.823416 1.8534 0.89064 1.99395 0.992919 2.10728L6.69597 8.41339C6.95846 8.70348 7.10389 9.0807 7.10407 9.47191V14.208Z" stroke="#364153" strokeWidth="1.5787" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Filters
            </Button>
          </div>
          <div className="top_right_flx flex-wrap" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {currentRoles?.some((role) => role.name === ROLES.OWNER || role.name === ROLES.PROGRAM_MANAGER) && (
              <>
                <Button
                  bsPrefix="custom-btn"
                  onClick={openTransferParticipantModal}
                  style={{
                    height: "2.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0.83331rem 1.52775rem",
                    borderRadius: "0.625rem",
                    background: "#F9F6FF",
                    color: "#7E57C2",
                    border: "none",
                    textAlign: "center",
                    fontFamily: "Poppins",
                    fontSize: "1rem",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "1.2"
                  }}
                >
                  Transfer Participant
                </Button>
                <Button
                  bsPrefix="custom-btn"
                  onClick={openAddParticipantModal}
                  style={{
                    height: "2.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0.83331rem 1.52775rem",
                    borderRadius: "0.625rem",
                    background: "#7E57C2",
                    color: "#FFF",
                    textAlign: "center",
                    fontFamily: "Poppins",
                    fontSize: "1rem",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "1.2",
                  }}
                >
                  Add Participant
                </Button>
              </>
            )}
          </div>
        </div>

        <div style={{ height: "1px", backgroundColor: "#E5E7EB", margin: "1.25rem -1.25rem 2.5rem -1.25rem", width: "calc(100% + 2.5rem)" }} />

        <ParticipantElementsFilter
          show={showFilters}
          onHide={() => setShowFilters(false)}
          
          // Use PENDING State
          caseworkerOptions={caseworkerOptions}
          selectedCaseworkerIds={pendingCaseworkerIds}
          onCaseworkerChange={setPendingCaseworkerIds}
          
          waiverOptions={waiverOptions}
          selectedWaiverIds={pendingWaiverIds}
          onWaiverChange={setPendingWaiverIds}
          
          regionOptions={regionOptions}
          selectedRegionId={pendingRegionId}
          onRegionChange={(val) => setPendingRegionId(val === "" ? null : parseInt(val))}
          
          selectedEmailLinked={pendingEmailLinked}
          onEmailLinkedChange={setPendingEmailLinked}
          
          onApply={handleApplyFilters} // Apply Action

          onReset={() => {
            setSearchTerm("");
            setPendingCaseworkerIds([]);
            setPendingWaiverIds([]);
            setPendingRegionId(null);
            setPendingEmailLinked("");
          }}
        />

        {loading ? (
          <div className="text-center p-4">Loading caseworkers...</div>
        ) : caseworkerSections.length === 0 ? (
          <div className="text-center p-4">
            {debouncedSearchTerm || selectedWaiverIds.length > 0 || selectedRegionId || selectedEmailLinked ? "No participants found matching current filters" : "No caseworkers with participants found"}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {caseworkerSections.map((section) => {
              const isHighlighted = (debouncedSearchTerm.trim().length > 0 || selectedWaiverIds.length > 0) && section.isExpanded;

              return (
                <div
                  key={section.id}
                  style={{
                    background: "white",
                    borderRadius: "0.5rem",
                    border: "1px solid #E5E7EB",
                    borderLeft: isHighlighted ? "4px solid #7E57C2" : "1px solid #E5E7EB",
                    overflow: section.isExpanded ? "visible" : "hidden",
                    transition: "border 0.3s ease"
                  }}
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "1rem 1.25rem",
                      background: "white",
                      border: "none",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#101828", margin: 0 }}>
                        {section.name}
                      </h3>
                      {section.roles && section.roles.length > 0 && (
                        <span className="w-fit bg-[#F9F6FF] border rounded-[3px] px-3 min-w-32">
                          {getHighestRole(section.roles) || section.roles.join(", ")}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "0.5rem", alignItems: "center" }}>
                      <span
                        style={{
                          padding: "0.25rem 0.75rem",
                          marginRight: "2rem",
                          fontSize: "0.87156rem",
                          color: "#7E57C2",
                          background: "#F9F6FF",
                          borderRadius: "0.75rem",
                          fontWeight: 400,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {section.participantCount || 0} participant{(section.participantCount || 0) !== 1 ? "s" : ""}
                      </span>
                      <svg
                        width="1.25rem"
                        height="1.25rem"
                        viewBox="0 0 20 20"
                        fill="none"
                        style={{
                          transform: section.isExpanded ? "rotate(-90deg)" : "rotate(90deg)",
                          transition: "transform 0.2s",
                          flexShrink: 0,
                        }}
                      >
                        <path d="M7.5 15L12.5 10L7.5 5" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </button>

                  {section.isExpanded && (
                    <div style={{ position: "relative", overflow: "visible" }}>
                      {section.isLoading ? (
                        <div style={{ padding: "2.5rem", textAlign: "center", color: "#6B7280" }}>
                          Loading participants...
                        </div>
                      ) : section.participants.length === 0 ? (
                        <div style={{ padding: "2.5rem", textAlign: "center", color: "#6B7280" }}>
                          No participants found matching current filters
                        </div>
                      ) : (
                        section.participants
                          .sort((a, b) => a.lastName.localeCompare(b.lastName))
                          .map((participant, index) => (
                            <div
                              key={participant.id}
                              style={{
                                padding: "1rem 1.25rem",
                                borderBottom: index < section.participants.length - 1 ? "1px solid #F3F4F6" : "none",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                            >
                              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr 1fr 20rem", gap: "1rem", alignItems: "center", width: "100%" }}>
                                <label style={{ position: "relative", display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}>
                                  <input
                                    type="checkbox"
                                    checked={participantToggles[participant.id] ?? true}
                                    onChange={() => handleToggleParticipant(participant.id.toString())}
                                    style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                                  />
                                  <div style={{ width: "2.75rem", height: "1.5rem", background: participantToggles[participant.id] ?? true ? "#7E57C2" : "#A9A9A9", borderRadius: "0.75rem", position: "relative", transition: "background-color 0.2s" }}>
                                    <div style={{ position: "absolute", top: "0.125rem", left: participantToggles[participant.id] ?? true ? "1.375rem" : "0.125rem", width: "1.25rem", height: "1.25rem", background: "white", borderRadius: "50%", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                                  </div>
                                </label>

                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: "1rem", color: "#6B7280", marginBottom: "0.125rem", letterSpacing: "0.025em" }}>Name</div>
                                  <div style={{ fontSize: "1rem", fontWeight: 500, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {participant.firstName} {participant.lastName}
                                  </div>
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: "1rem", color: "#364153", marginBottom: "0.125rem", letterSpacing: "0.025em" }}>Email</div>
                                  <div style={{ fontSize: "1rem", color: "#364153", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {participant.email || "-"}
                                  </div>
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: "1rem", color: "#6B7280", marginBottom: "0.125rem", letterSpacing: "0.025em" }}>Waiver</div>
                                  <div style={{ fontSize: "1rem", color: "#364153", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {participant.waiverName}
                                  </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", flexWrap: "nowrap", justifyContent: "flex-end", minWidth: "fit-content" }}>
                                  <button
                                    onClick={() => linkEmail(participant.id)}
                                    style={{
                                      padding: "0rem 0.675rem", background: "#7E57C2", color: "#FFF", height: "1.875rem", lineHeight: "1.20863rem", fontSize: "1rem", fontWeight: 400, border: "none", borderRadius: "0.3125rem", cursor: "pointer", transition: "background-color 0.2s", whiteSpace: "nowrap",
                                      visibility: !participant.email ? "visible" : "hidden", width: !participant.email ? "auto" : "6.5rem",
                                    }}
                                    onMouseEnter={(e) => !participant.email && (e.currentTarget.style.backgroundColor = "#7c3aed")}
                                    onMouseLeave={(e) => !participant.email && (e.currentTarget.style.backgroundColor = "#7E57C2")}
                                  >
                                    Link Account
                                  </button>

                                  {(participant && participant?.waiverName && BUMP_BUTTON_ALLOWED_FOR_WAIVER_OPTIONS?.includes(participant?.waiverName)) && (
                                    <button
                                      onClick={() => { setBumpingParticipant(participant); setIsBumpParticipantModalOpen(true); }}
                                      style={{ padding: "0rem 0.675rem", height: "1.875rem", lineHeight: "1.20863rem", background: "#7E57C2", color: "#FFF", fontSize: "1rem", fontWeight: 400, border: "none", borderRadius: "0.3125rem", cursor: "pointer", transition: "background-color 0.2s", whiteSpace: "nowrap" }}
                                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#7c3aed")}
                                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#7E57C2")}
                                    >
                                      Waiver Bump
                                    </button>
                                  )}

                                  <div style={{ position: "relative" }}>
                                    <button
                                      ref={(el) => { if (el) menuButtonRefs.current[participant.id.toString()] = el; }}
                                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === participant.id.toString() ? null : participant.id.toString()); }}
                                      style={{ background: "transparent", border: "none", cursor: "pointer", padding: "0.25rem", display: "flex", alignItems: "center", justifyContent: "center" }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <circle cx="10" cy="5" r="1.5" fill="#364153" />
                                        <circle cx="10" cy="10" r="1.5" fill="#364153" />
                                        <circle cx="10" cy="15" r="1.5" fill="#364153" />
                                      </svg>
                                    </button>
                                    {openMenuId === participant.id.toString() && (
                                      <div
                                        ref={(el) => { if (el) menuRefs.current[participant.id.toString()] = el; }}
                                        style={{ position: "fixed", background: "white", borderRadius: "0.5rem", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", border: "1px solid #E5E7EB", zIndex: 9999, minWidth: "12rem", padding: "0.25rem 0" }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); setEditingParticipantId(participant.id); setOpenMenuId(null); setIsEditParticipantModalOpen(true); }}
                                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.625rem 1rem", background: "transparent", border: "none", cursor: "pointer", fontSize: "0.875rem", color: "#364153", textAlign: "left" }}
                                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#F9FAFB"; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#364153" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" /><path d="M12.2499 1.75015C12.5151 1.48493 12.8748 1.33594 13.2499 1.33594C13.625 1.33594 13.9847 1.48493 14.2499 1.75015C14.5151 2.01537 14.6641 2.37508 14.6641 2.75015C14.6641 3.12522 14.5151 3.48493 14.2499 3.75015L8.24123 9.75948C8.08293 9.91765 7.88737 10.0334 7.67257 10.0962L5.75723 10.6562C5.69987 10.6729 5.63906 10.6739 5.58117 10.6591C5.52329 10.6442 5.47045 10.6141 5.4282 10.5719C5.38594 10.5296 5.35583 10.4768 5.341 10.4189C5.32617 10.361 5.32717 10.3002 5.3439 10.2428L5.9039 8.32748C5.96692 8.11285 6.08292 7.91752 6.24123 7.75948L12.2499 1.75015Z" stroke="#364153" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" /></svg>
                                          Edit Participant
                                        </button>
                                        <button
                                          onClick={() => { setSelectedParticipantForTransfer(participant); setIsTransferParticipantModalOpen(true); setOpenMenuId(null); }}
                                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.625rem 1rem", background: "transparent", border: "none", cursor: "pointer", fontSize: "0.875rem", color: "#364153", textAlign: "left" }}
                                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#F9FAFB"; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 10.995V13.2503C2 13.4492 2.07902 13.64 2.21968 13.7807C2.36033 13.9213 2.5511 14.0003 2.75002 14.0003H13.2503C13.4492 14.0003 13.64 13.9213 13.7807 13.7807C13.9213 13.64 14.0003 13.4492 14.0003 13.2503V10.995M13.5143 7.57715L11.229 9.97572L8.94295 7.57715" stroke="#364153" stroke-linecap="round" stroke-linejoin="round" /><path d="M3.7998 9.46871V5.71411C3.79961 5.22631 3.89554 4.74325 4.08212 4.29255C4.2687 3.84184 4.54228 3.43232 4.8872 3.0874C5.23213 2.74247 5.64165 2.4689 6.09235 2.28232C6.54305 2.09574 7.02611 1.9998 7.51391 2C8.00177 1.9997 8.48491 2.09557 8.9357 2.2821C9.38649 2.46864 9.7961 2.7422 10.1411 3.08713C10.4861 3.43207 10.7597 3.84162 10.9464 4.29237C11.133 4.74313 11.229 5.22624 11.2288 5.71411V9.46871" stroke="#364153" stroke-linecap="round" stroke-linejoin="round" /></svg>
                                          Transfer
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {isEmailModalOpen && (
        <EmailModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSave={handleSaveEmail}
          email={selectedEmail}
          setEmail={setSelectedEmail}
        />
      )}

      <TransferParticipantModal
        isOpen={isTransferParticipantModalOpen}
        onClose={() => {
          setIsTransferParticipantModalOpen(false);
          setSelectedParticipantForTransfer(null);
        }}
        onSuccess={handleParticipantCreated}
        participant={selectedParticipantForTransfer}
        participants={selectedParticipantForTransfer ? undefined : getAllParticipantsForTransfer()}
      />

      <AddParticipantModal
        isOpen={isAddParticipantModalOpen}
        onClose={() => setIsAddParticipantModalOpen(false)}
        onSuccess={handleParticipantCreated}
      />

      <EditParticipantModal
        isOpen={isEditParticipantModalOpen}
        onClose={() => {
          setIsEditParticipantModalOpen(false);
          setEditingParticipantId(null);
        }}
        onSuccess={handleParticipantCreated}
        participantId={editingParticipantId}
      />

      <BumpParticipantModal
        isOpen={isBumpParticipantModalOpen}
        onClose={() => {
          setIsBumpParticipantModalOpen(false);
          setBumpingParticipant(null);
        }}
        onSuccess={handleParticipantCreated}
        participant={bumpingParticipant}
      />
    </>
  );
};

export default OwnerParticipantsElement;